import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button } from "@h2j-data-team/ui";

import type { QlikAuthMethod } from "@repo/shared";

const QLIK_DOCS_URL = "https://qlik.dev/authenticate/oauth/create-oauth-client/";

const OAUTH_SCOPES = ["admin.apps", "admin.spaces:read", "admin.users:read"] as const;

type ConnectionDrawerHelpProps = {
  authMethod: QlikAuthMethod;
};

/**
 * Compact, inline replacement for the page-level help aside — fits inside the
 * connection form drawer. Shows what we read + (for OAuth) the required scopes
 * and a link to the full Qlik setup guide.
 */
export function ConnectionDrawerHelp({ authMethod }: ConnectionDrawerHelpProps) {
  const { t } = useTranslation("connections");
  const isOauth = authMethod === "oauth_client_credentials";
  const whatItems = t("aside.whatGetsRead.items", { returnObjects: true }) as string[];

  return (
    // A static note: no announcement when the drawer opens.
    <Alert tone="info" role="note" title={t("aside.whatGetsRead.title")}>
      {Array.isArray(whatItems) && whatItems.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-5">
          {whatItems.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}

      {isOauth ? (
        <div className="mt-3 space-y-1.5 border-t border-hairline pt-2.5">
          <p className="font-medium text-ink">{t("aside.scopes.title")}</p>
          <ul className="flex flex-wrap gap-1">
            {OAUTH_SCOPES.map((scope) => (
              <li key={scope}>
                <Badge variant="outline" className="font-mono">
                  {scope}
                </Badge>
              </li>
            ))}
          </ul>
          <Button asChild variant="link" size="compact" className="px-0">
            <a href={QLIK_DOCS_URL} target="_blank" rel="noopener noreferrer">
              {t("aside.scopes.openDocumentation")}
              <ExternalLink />
            </a>
          </Button>
        </div>
      ) : null}
    </Alert>
  );
}
